import { z } from 'zod';
import {
  assetRefSchema,
  COLOR_FAMILIES,
  MAX_MIN_ORDER_QTY,
  MAX_RECOMMENDED_PRODUCTS,
  MAX_UNIT_PRICE,
  productColorOptionSchema,
  productImageSchema,
  productSizeOptionSchema,
} from './catalog';
import { uuidSchema } from './common';

// ---------------------------------------------------------------------------
// 스토어 공개 계약 — 로그인 없이 노출된다(publicProcedure).
// 어드민 계약(catalog.ts)과 별도인 이유: 공개 응답에는 status·감사·운영 필드를
// 싣지 않는 것을 스키마 수준에서 보장하기 위해서다.
// ---------------------------------------------------------------------------

export const PUBLIC_PRODUCT_SORTS = ['RECOMMENDED', 'PRICE_ASC', 'PRICE_DESC'] as const;
export type PublicProductSort = (typeof PUBLIC_PRODUCT_SORTS)[number];

export const PUBLIC_PRODUCTS_DEFAULT_LIMIT = 20;
export const PUBLIC_PRODUCTS_MAX_LIMIT = 100;

/**
 * 최소 주문 수량 상한 필터의 상한 — 어드민 입력 상한(MAX_MIN_ORDER_QTY)과 같은 값.
 * 상품이 가질 수 없는 수량으로 필터해도 의미가 없고, INT4 범위 밖은 DB 오류(5xx)가 된다.
 */
export const PUBLIC_MAX_MIN_ORDER_QTY = MAX_MIN_ORDER_QTY;

/** 페이지 번호 상한 — 무의미하게 큰 OFFSET 스캔을 경계에서 거부한다. */
export const PUBLIC_PRODUCTS_MAX_PAGE = 10_000;

export const listPublicProductsInputSchema = z
  .object({
    /** 소분류·대분류 모두 허용 — 대분류면 서버가 하위 소분류 전체로 확장한다. */
    categoryId: uuidSchema.optional(),
    /** 기획전 카드에서 넘어온 필터 — 공개 기획전에 묶인 상품만 남긴다. */
    exhibitionId: uuidSchema.optional(),
    /** "N개 이하로 만들 수 있는 상품" 필터. */
    minOrderQtyMax: z.number().int().min(1).max(PUBLIC_MAX_MIN_ORDER_QTY).optional(),
    unitPriceMin: z.number().int().min(0).max(MAX_UNIT_PRICE).optional(),
    unitPriceMax: z.number().int().min(0).max(MAX_UNIT_PRICE).optional(),
    /** 수급 가능한 색상 기준으로 매칭한다(단종 색만 가진 상품은 제외). */
    colorFamilies: z.array(z.enum(COLOR_FAMILIES)).min(1).max(COLOR_FAMILIES.length).optional(),
    sort: z.enum(PUBLIC_PRODUCT_SORTS).default('RECOMMENDED'),
    /** 1부터 시작하는 페이지 번호(번호형 페이지네이션). */
    page: z.number().int().min(1).max(PUBLIC_PRODUCTS_MAX_PAGE).default(1),
    limit: z
      .number()
      .int()
      .min(1)
      .max(PUBLIC_PRODUCTS_MAX_LIMIT)
      .default(PUBLIC_PRODUCTS_DEFAULT_LIMIT),
  })
  // 역전된 범위(min > max)는 항상 빈 목록이 되어 "상품 없음"으로 오인된다 — 경계에서 거부.
  .refine(
    (input) =>
      input.unitPriceMin === undefined ||
      input.unitPriceMax === undefined ||
      input.unitPriceMin <= input.unitPriceMax,
    { message: '단가 범위가 올바르지 않습니다.', path: ['unitPriceMax'] },
  );
export type ListPublicProductsInput = z.infer<typeof listPublicProductsInputSchema>;

export const publicProductSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  minOrderQty: z.number().int(),
  /** 최소 주문 수량 구간의 개당 단가(원). PUBLISHED 상품은 항상 존재한다(DB CHECK). */
  baseUnitPrice: z.number().int(),
  thumbnail: assetRefSchema.nullable(),
  printMethodNames: z.array(z.string()),
  /** 수급 가능한(isAvailable) 색상 수 — 단종 색상은 세지 않는다(확정 결정). */
  availableColorCount: z.number().int().nonnegative(),
});
export type PublicProductSummary = z.infer<typeof publicProductSummarySchema>;

export const publicProductListSchema = z.object({
  items: z.array(publicProductSummarySchema),
  /** 조건에 맞는 전체 상품 수 — 페이지 수 계산·"전체 N개" 표기에 쓴다. */
  totalCount: z.number().int().nonnegative(),
  snapshotId: uuidSchema.nullable(),
});
export type PublicProductList = z.infer<typeof publicProductListSchema>;

/**
 * HOME 추천 목록 — 추천 순위순 → 최신 공개 상품 채우기, 최대 MAX_RECOMMENDED_PRODUCTS 개.
 * 배열 순서가 노출 순서다(1번째가 히어로 카드).
 */
export const homeRecommendedProductListSchema = z
  .array(publicProductSummarySchema)
  .max(MAX_RECOMMENDED_PRODUCTS);
export type HomeRecommendedProductList = z.infer<typeof homeRecommendedProductListSchema>;

/** 상세 브레드크럼용 대표 카테고리 — "대분류 · 소분류" 표기에 쓴다. */
export const publicPrimaryCategorySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  parentName: z.string().nullable(),
});

export const publicPriceTierSchema = z.object({
  minQty: z.number().int(),
  unitPrice: z.number().int(),
});
export type PublicPriceTier = z.infer<typeof publicPriceTierSchema>;

export const publicProductDetailSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  minOrderQty: z.number().int(),
  leadTimeNote: z.string(),
  materialNote: z.string(),
  /** PUBLISHED 는 항상 존재(DB CHECK). ACTIVE ADMIN 프리뷰(DRAFT)에서만 null 일 수 있다. */
  widthMm: z.number().nullable(),
  heightMm: z.number().nullable(),
  productionNote: z.string(),
  /** PUBLISHED 는 항상 존재(DB CHECK). ACTIVE ADMIN 프리뷰(DRAFT)에서만 null 일 수 있다. */
  baseUnitPrice: z.number().int().nullable(),
  primaryCategory: publicPrimaryCategorySchema.nullable(),
  /** displayOrder 오름차순. 1번이 대표 이미지, 라벨은 없다(확정 결정). */
  images: z.array(productImageSchema),
  detailImage: assetRefSchema.nullable(),
  printMethodNames: z.array(z.string()),
  /** 최신 편집영역 유무 — false 면 상세가 [견적 요청] 버튼을 보인다. */
  hasEditArea: z.boolean(),
  /** 단종 색상도 내려간다 — UI 는 목록에 남기고 선택만 막는다(와이어프레임 원칙). */
  colorOptions: z.array(productColorOptionSchema),
  /** 사이즈 옵션 — 없으면 빈 배열(사이즈 없는 상품). */
  sizeOptions: z.array(productSizeOptionSchema),
  /** minQty 오름차순. */
  priceTiers: z.array(publicPriceTierSchema),
});
export type PublicProductDetail = z.infer<typeof publicProductDetailSchema>;

/**
 * 상품 상세 조회 기록(products.recordView, SUPER-82) — 로그인 ACTIVE 회원만 부른다. 응답은 없다: 기록했는지, 상품이 공개인지
 * 알려 주지 않는다(관리자·비공개 상품은 서버가 조용히 건너뛴다).
 */
export const recordProductViewInputSchema = z.strictObject({ productId: uuidSchema });
export type RecordProductViewInput = z.infer<typeof recordProductViewInputSchema>;
